import { documentsAPI, certificateTemplatesAPI } from '../../services/api';
import { showToast } from '../common/Toast';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentTypesAPI } from '../../services/api';
import DocumentTypeModal from './DocumentTypeModal';
import CertificateTemplatesPage from './CertificateTemplatesPage';
import ResidentAutocomplete from './ResidentAutocomplete';
import CertificatePreview from '../certificate/CertificatePreview';
import ReactQuill from 'react-quill';
import { renderTemplate } from '../../utils/templateRenderer';
import 'react-quill/dist/quill.snow.css';


// Utility to capitalize strings
const capitalize = (str) =>
  typeof str === 'string' && str.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : '';

// Utility to format birthdate (YYYY-MM-DD to MM/DD/YYYY or similar)
const formatBirthdate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// Utility to format long date (e.g., July 16, 2025)
const formatLongDate = (date) => {
  if (!date) return '';
  try {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

// Compose preview data for template variables
const getPreviewData = (doc) => {
  const resident = doc.resident || {};
  // Compose resident name as "Firstname M. Lastname Suffix"
  const middleInitial = resident.middle_name
    ? `${resident.middle_name.charAt(0).toUpperCase()}.`
    : '';
  const suffix = resident.suffix ? ` ${resident.suffix}` : '';
  const residentName =
    resident.first_name && resident.last_name
      ? `${capitalize(resident.first_name)}${middleInitial ? ' ' + middleInitial : ''} ${capitalize(resident.last_name)}${suffix}`
      : doc.resident_name || 'N/A';
  // Always fill out all fields with a default value if missing
  return {
    resident_name: residentName || '-- Resident Name --',
    address: resident.full_address || '-- Address --',
    age: resident.age !== undefined && resident.age !== null && resident.age !== '' ? resident.age : '-- Age --',
    civil_status: capitalize(resident.civil_status || '-- Civil Status --'),
    gender: capitalize(resident.gender || '-- Gender --'),
    pronouns: resident.gender === 'male' ? 'He/Him' : resident.gender === 'female' ? 'She/Her' : 'They/Them',
    birthdate: formatBirthdate(resident.birthdate || '') || '-- Birthdate --',
    birthplace: resident.birthplace || '-- Birthplace --',
    nationality: resident.nationality || '-- Nationality --',
    occupation: resident.occupation || '-- Occupation --',
    purpose: doc.purpose || '-- Purpose --',
    issued_by: 'Barangay Captain',
    date: formatLongDate(doc.issued_at ? new Date(doc.issued_at) : new Date()) || '-- Date --',
    tracking_number: doc.tracking_number || '-- Tracking Number --',
    fee_amount: doc.fee_amount !== undefined && doc.fee_amount !== null && doc.fee_amount !== ''
      ? `₱ ${parseFloat(doc.fee_amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '₱ 0.00',
    remarks: doc.remarks || '-- Remarks --',
    photo_url: resident.photo_url || null,
  };
};
// Delete document request utility


/**
 * Delete a document request by ID
 * @param {string|number} documentId
 * @param {function} refetchDocuments - Call this after delete to refresh the table
 * @param {function} [onError] - Optional callback on error
 *
 * Usage:
 *   deleteDocumentRequest(id, fetchDocuments)
 */
export async function deleteDocumentRequest(documentId, refetchDocuments, onError) {
  try {
    await documentsAPI.delete(documentId);
    showToast('Document deleted successfully.', 'success');
    if (typeof refetchDocuments === 'function') refetchDocuments();
  } catch (err) {
    showToast('Failed to delete document.', 'error');
    if (onError) onError(err);
  }
}


const initialForm = {
  resident_id: '',
  resident_name: '',
  is_non_resident: false,
  document_type_id: '',
  type: 'barangay_clearance', // for select only
  purpose: '',
  issued_at: '',
  remarks: '',
  status: 'pending'
};



const DocumentFormModal = ({ open, onClose, onSubmit, initialData }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [typeOptions, setTypeOptions] = useState([]);
  const [feeAmount, setFeeAmount] = useState('');
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showCertificateTemplateModal, setShowCertificateTemplateModal] = useState(false);
  const [templateHtml, setTemplateHtml] = useState('');
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [renderedHtml, setRenderedHtml] = useState('');

  // Add scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .document-form-modal-scrollbar::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .document-form-modal-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .document-form-modal-scrollbar::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;
      }
      .document-form-modal-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a8a8a8;
      }

      /* Quill preview border fix: strip Quill's own borders, let the wrapper handle it */
      .doc-preview-quill-wrapper .ql-container.ql-snow {
        border: none !important;
      }
      .doc-preview-quill-wrapper .ql-editor {
        padding: 1.5rem !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  // Fetch template when document_type_id changes
  useEffect(() => {
    if (!form.document_type_id) {
      setTemplateHtml('');
      setCurrentTemplate(null);
      return;
    }
    setLoadingTemplate(true);
    // First get all templates, then find the one matching the document type
    certificateTemplatesAPI.getAll()
      .then(({ data }) => {
        const templates = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
        // Find template by document_type_id matching
        const matchingTemplate = templates.find(tpl => tpl.document_type_id === form.document_type_id);
        if (matchingTemplate) {
          setTemplateHtml(matchingTemplate.body || '');
          setCurrentTemplate(matchingTemplate);
        } else {
          setTemplateHtml('');
          setCurrentTemplate(null);
        }
      })
      .catch(() => {
        setTemplateHtml('');
        setCurrentTemplate(null);
      })
      .finally(() => setLoadingTemplate(false));
  }, [form.document_type_id]);

  // Replace template variables with form values using enhanced template renderer
  useEffect(() => {
    if (!templateHtml) {
      setRenderedHtml('');
      return;
    }
    const previewData = getPreviewData(form);
    const html = renderTemplate(templateHtml, previewData);
    setRenderedHtml(html);
  }, [templateHtml, form]);
  // Fetch document types from CMS
  useEffect(() => {
    setLoadingTypes(true);
    documentTypesAPI.getAllV1()
      .then(({ data }) => {
        setTypeOptions(data.data.map(t => ({ value: t.key, label: t.label, fee_amount: t.fee_amount, id: t.id }))); // include id
      })
      .catch(() => setTypeOptions([]))
      .finally(() => setLoadingTypes(false));
  }, []);

  useEffect(() => {
    if (initialData && typeOptions.length > 0) {
      // If resident object exists, use its id and name for the form
      let resident_id = initialData.resident_id;
      let resident_name = initialData.resident_name;
      let is_non_resident = initialData.is_non_resident;
      let type = initialData.document_type || initialData.type || '';
      let document_type_id = initialData.document_type_id || '';
      let fee_amount = '';
      if (initialData.resident) {
        resident_id = initialData.resident.id;
        resident_name = `${initialData.resident.first_name || ''} ${initialData.resident.last_name || ''}`.trim();
        is_non_resident = false;
      }
      // Try to match by id first, then by key
      let found = typeOptions.find(opt => opt.id === document_type_id);
      if (!found) {
        found = typeOptions.find(opt => opt.value === type);
      }
      if (found) {
        document_type_id = found.id;
        type = found.value;
        fee_amount = found.fee_amount;
      } else {
        // fallback to first option if not found
        const first = typeOptions[0];
        document_type_id = first.id;
        type = first.value;
        fee_amount = first.fee_amount;
      }
      setForm({
        ...initialForm,
        ...initialData,
        resident_id,
        resident_name,
        is_non_resident,
        type,
        document_type_id,
        fee_amount: fee_amount !== '' ? fee_amount : initialData.fee_amount || ''
      });
      setFeeAmount(fee_amount !== '' ? fee_amount : initialData.fee_amount || '');
    } else if (!initialData && typeOptions.length > 0) {
      // Set default to first type option only if not editing
      const first = typeOptions[0];
      setForm(prev => ({
        ...initialForm,
        type: first.value,
        document_type_id: first.id,
        fee_amount: first.fee_amount
      }));
      setFeeAmount(first.fee_amount);
    } else {
      setForm(initialForm);
      setFeeAmount('');
    }
  }, [initialData, open, typeOptions]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'type') {
      const found = typeOptions.find(opt => opt.value === value);
      const feeAmountValue = found ? found.fee_amount : '';
      setForm((prev) => ({
        ...prev,
        type: value,
        document_type_id: found ? found.id : '',
        fee_amount: feeAmountValue
      }));
      setFeeAmount(feeAmountValue);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Resident autocomplete handler
  const handleResidentChange = (resident) => {
    if (resident) {
      setForm((prev) => ({
        ...prev,
        resident_id: resident.id,
        resident_name: resident.name,
        is_non_resident: false,
        resident: resident // Attach full resident object for preview
      }));
    } else {
      setForm((prev) => ({ ...prev, resident_id: '', resident_name: '', is_non_resident: false, resident: undefined }));
    }
  };

  // Manual add handler
  const handleManualAdd = (name) => {
    setForm((prev) => ({
      ...prev,
      resident_id: '',
      resident_name: name,
      is_non_resident: true
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only send document_type_id, not type string
    const payload = {
      ...form,
      document_type_id: form.document_type_id,
    };
    delete payload.type;
    onSubmit(payload);
  };

  if (!open) return null;


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-[1100px] h-[90vh] max-h-[95vh] p-4 sm:p-6 md:p-8 relative flex flex-col overflow-hidden">
        <div className="flex justify-between items-center mb-4 sm:mb-6 flex-shrink-0 animate-in slide-in-from-top duration-500 delay-200">
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
            {initialData ? 'Edit Document' : 'Add Barangay Clearance/Certificate'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95"
          >
            ×
          </button>
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-4 overflow-hidden" style={{ minHeight: 0 }}>
          {/* Left: Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 overflow-y-auto pr-2 document-form-modal-scrollbar" style={{ width: '100%', maxWidth: '100%' }}>
            {/* Resident Name - first row, full width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Resident Name</label>
              <ResidentAutocomplete
                value={form.resident_id ? { id: form.resident_id, name: form.resident_name } : form.resident_name ? { name: form.resident_name } : null}
                onChange={handleResidentChange}
                onManualAdd={handleManualAdd}
                disabled={!!initialData}
              />
              {form.is_non_resident && (
                <div className="text-xs text-yellow-600 mt-1">Non-resident will be tagged for this document.</div>
              )}
            </div>
            {/* Document Type & Fee Amount */}
            <div className="grid grid-cols-1 gap-8">
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                  <button
                    type="button"
                    className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition"
                    onClick={() => setShowCertificateTemplateModal(true)}
                  >
                    + Add New Document Type
                  </button>
                </div>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300"
                  required
                  disabled={loadingTypes}
                >
                  <option value="" disabled>{loadingTypes ? 'Loading types...' : 'Select document type'}</option>
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee Amount</label>
                <input
                  name="fee_amount"
                  type="number"
                  min="0"
                  step="1"
                  value={form.fee_amount !== undefined ? form.fee_amount : feeAmount}
                  onChange={e => setForm(prev => ({ ...prev, fee_amount: e.target.value }))}
                  className="block w-full border-2 border-gray-200 rounded-lg px-4 py-2 text-gray-900 placeholder-gray-400"
                  required
                />
              </div>
            </div>
            {/* Purpose */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
              <textarea
                name="purpose"
                value={form.purpose ?? ''}
                onChange={handleChange}
                className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 min-h-[80px] text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300 resize-y"
                required
                placeholder="State the purpose for this document..."
                maxLength={500}
              />
              <div className="text-xs text-gray-400 text-right mt-1">{(form.purpose ? form.purpose.length : 0)}/500</div>
            </div>
            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                name="remarks"
                value={form.remarks ?? ''}
                onChange={handleChange}
                className="mt-1 block w-full border-2 border-gray-200 rounded-lg px-4 py-3 min-h-[60px] text-gray-900 placeholder-gray-500 transition-all duration-200 group-hover:border-gray-300 resize-y"
                placeholder="Additional notes (optional)"
                maxLength={300}
              />
              <div className="text-xs text-gray-400 text-right mt-1">{(form.remarks ? form.remarks.length : 0)}/300</div>
            </div>
            {/* Actions */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 animate-in slide-in-from-bottom duration-500 delay-500">
              <button
                type="button"
                onClick={onClose}
                disabled={form.loading}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={form.loading}
                className="px-6 py-3 border-2 border-transparent rounded-lg text-white font-medium bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-200 hover:scale-105 active:scale-95 relative overflow-hidden group"
              >
                {form.loading ? (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.964 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                ) : null}
                {initialData ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
          {/* Right: Certificate Preview */}
          <div className="flex flex-col h-full w-full relative overflow-hidden">
            <div className="flex-1 flex flex-col document-form-modal-scrollbar">
              <div className="w-full">
                {currentTemplate ? (
                  <div
                    className="certificate-preview-a4 bg-white rounded-lg overflow-auto"
                    style={{
                      border:     '1px solid #d1d5db',
                      minHeight:  '200mm',
                      maxWidth:   '90%',
                      margin:     '0 auto',
                      boxShadow:  '0 0 8px 0 rgba(0,0,0,0.04)',
                    }}
                  >
                    <CertificatePreview 
                      template={currentTemplate} 
                      data={getPreviewData(form)} 
                      showPrint={false} 
                    />
                  </div>
                ) : (
                  <div
                    className="doc-preview-quill-wrapper bg-white shadow-md rounded-lg overflow-hidden"
                    style={{ border: '2px solid #d1d5db' }}
                  >
                    <ReactQuill
                      value={renderedHtml}
                      readOnly={true}
                      theme="snow"
                      style={{ width: '100%', maxWidth: '100%' }}
                      modules={{ toolbar: false }}
                    />
                  </div>
                )}
              </div>
              {/* Overlay loading indicator inside right portion */}
              {loadingTemplate && (
                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                  <div className="flex flex-col items-center">
                    <svg className="animate-spin h-7 w-7 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-blue-600 font-medium">Loading template...</span>
                  </div>
                </div>
              )}
              {!loadingTemplate && !currentTemplate && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 z-10 overflow-hidden">
                  <div className="flex flex-col items-center justify-center p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200 bg-white/80 animate-in fade-in duration-500 max-w-full mx-4">
                    <svg className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-7 0a2 2 0 002 2h6a2 2 0 002-2v-2a2 2 0 00-2-2h-2a2 2 0 00-2 2v2m-7 0V7a2 2 0 012-2h10a2 2 0 012 2v10" />
                    </svg>
                    <span className="text-base sm:text-lg font-semibold text-gray-500 mb-1 text-center">No Template Available</span>
                    <span className="text-xs sm:text-sm text-gray-400 text-center px-2">There is no template for this document type.<br />Please select another type or contact your administrator.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Certificate Template Management Modal */}
      {showCertificateTemplateModal && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full min-w-[300mm] max-w-6xl relative p-6 max-h-[100vh]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium text-gray-900">Certificate Template</h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setShowCertificateTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95"
              >
                ×
              </button>
            </div>
            <CertificateTemplatesPage />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentFormModal;